import { ColumnFiltersState } from "@tanstack/react-table";
import { useCallback, useState } from "react";
import { useSearchParams } from "wouter";
import { ReactTable2Table } from "./features";

export interface UseTableFilters {
    columnFilters: ColumnFiltersState;
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
    hasActiveFilters: boolean;
    clearAllFilters: (table: ReactTable2Table) => void;
}

/**
 * Owns the table's column-filter state and the "clear all filters" behaviour.
 *
 * Individual filter components hydrate themselves from the URL, so clearing must also strip the
 * matching query params - otherwise those components immediately re-apply the values they read back.
 */
export const useTableFilters = (): UseTableFilters => {
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [, setQueryArgs] = useSearchParams();

    const clearAllFilters = useCallback(
        (table: ReactTable2Table) => {
            table.resetColumnFilters();
            setQueryArgs(
                (prev) => {
                    table.getAllLeafColumns().forEach((column) => prev.delete(column.id));
                    return prev;
                },
                { replace: true }
            );
        },
        [setQueryArgs]
    );

    return { columnFilters, setColumnFilters, hasActiveFilters: columnFilters.length > 0, clearAllFilters };
};
